import React, { useEffect } from 'react';
import { Button, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import * as Icon from 'react-feather';
import * as Yup from 'yup';

import FormInput from '../../../components/form/FormInput';
import FormNumberInput from '../../../components/form/FormNumberInput';
import FormTextarea from '../../../components/form/FormTextarea';

import {
  auditorCategoryAdd,
  auditorCategoryUpdate,
  auditorCategoryGetById
} from '../../../store/slices/tenantSlices/auditorSlice';

const AddEditAuditorCategoryModal = ({
  isOpen,
  onCloseClick,
  isEdit = false,
  editRecord = null
}) => {
  const dispatch = useDispatch();

  const initialValues = {
    category_name: '',
    subCategory: [{ score_weight: 0, criteria_description: '' }]
  };

  const validationSchema = Yup.object({
    category_name: Yup.string().required('Name is required'),
    subCategory: Yup.array()
      .of(
        Yup.object({
          score_weight: Yup.number()
            .required('Score weight is required')
            .min(1, 'Score must be greater than 0'),
          criteria_description: Yup.string().required('Description is required')
        })
      )
      .min(1, 'At least one subcategory is required')
  });

  const { values, handleChange, handleBlur, handleSubmit, touched, errors, resetForm, setValues } =
    useFormik({
      initialValues,
      validationSchema,
      onSubmit: async () => {
        const payload = { ...values, id: editRecord?.id };

        try {
          const response = await dispatch(
            isEdit ? auditorCategoryUpdate(payload) : auditorCategoryAdd(payload)
          ).unwrap();

          if (response && response?.message) {
            toast.success(`Record ${isEdit ? 'Updated' : 'Added'} Successfully`);
            onCloseClick(true);
            resetForm();
          } else {
            toast.error(response?.error || 'Something went wrong');
          }
        } catch (error) {
          console.error('error', error);
        }
      }
    });

  const fetchAuditorCategoryById = async () => {
    try {
      const response = await dispatch(auditorCategoryGetById(editRecord?.id)).unwrap();
      const category = [...(response?.subcategory || [])]?.map((val) => {
        return {
          score_weight: val?.score_weight,
          criteria_description: val?.criteria_description
        };
      });
      setValues({
        category_name: response?.category?.category_name,
        subCategory: category
      });
    } catch (error) {
      console.error('error', error);
    }
  };

  useEffect(() => {
    if (isEdit && editRecord?.id) {
      fetchAuditorCategoryById();
    } else {
      resetForm();
    }
  }, [editRecord]);

  const handleSubCatChange = (index, e) => {
    const { name, value } = e.target;
    const updatedSubCat = [...values.subCategory];
    updatedSubCat[index] = {
      ...updatedSubCat[index],
      [name]: name === 'score_weight' ? Number(value) : value
    };
    setValues({ ...values, subCategory: updatedSubCat });
  };

  const handleAddSubCat = () => {
    setValues({
      ...values,
      subCategory: [...values.subCategory, { score_weight: 0, criteria_description: '' }]
    });
  };

  const handleDeleteSubCat = (index) => {
    const updatedSubCat = [...values.subCategory];
    updatedSubCat.splice(index, 1);
    setValues({ ...values, subCategory: updatedSubCat });
  };

  return (
    <Modal isOpen={isOpen} size="xl" centered>
      <ModalHeader className="justify-content-center py-2">
        <div className="fs-5">{isEdit ? 'Edit Auditor Category' : 'Add Auditor Category'}</div>
      </ModalHeader>
      <ModalBody className="px-4">
        <form id="auditor-category" onSubmit={handleSubmit}>
          <Row>
            <Col lg="4">
              <FormInput
                name="category_name"
                label="Name"
                placeholder="Enter name"
                value={values.category_name}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
          </Row>
          <Row>
            <Col lg="12" className="border-lightGray" style={{ borderBottom: '1px solid' }}>
              <h5 className="mt-4 text-dark fw-bolder">Sub Category</h5>
            </Col>
          </Row>
          {values?.subCategory.map((item, index, array) => (
            <Row key={index} className="pt-3">
              <Col lg="3">
                <FormNumberInput
                  name="score_weight"
                  label="Score Weight"
                  placeholder="Enter score weight"
                  value={item.score_weight}
                  onChange={(e) => handleSubCatChange(index, e)}
                  onBlur={handleBlur}
                  touched={touched?.subCategory?.[index]}
                  errors={errors?.subCategory?.[index]}
                />
              </Col>
              <Col lg="6">
                <FormTextarea
                  name="criteria_description"
                  label="Criteria"
                  placeholder="Enter Criteria"
                  rows={1}
                  value={item.criteria_description}
                  onChange={(e) => handleSubCatChange(index, e)}
                  onBlur={handleBlur}
                  touched={touched?.subCategory?.[index]}
                  errors={errors?.subCategory?.[index]}
                />
              </Col>
              <Col lg="3" className="d-flex align-items-center gap-4 pt-2">
                {array?.length > 1 && (
                  <Icon.Trash
                    size={20}
                    onClick={() => handleDeleteSubCat(index)}
                    className="cursor-pointer text-danger"
                  />
                )}
                {array?.length - 1 === index && (
                  <Icon.Plus
                    className="cursor-pointer text-info"
                    size={20}
                    onClick={handleAddSubCat}
                  />
                )}
              </Col>
            </Row>
          ))}
        </form>
      </ModalBody>
      <ModalFooter>
        <Button
          onClick={() => {
            onCloseClick();
            resetForm();
          }}
          className="px-5 bg-transparent text-primary">
          Cancel
        </Button>
        <Button form="auditor-category" type="submit" className="px-5 bg-primary border-primary">
          {isEdit ? 'Update' : 'Save'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

AddEditAuditorCategoryModal.propTypes = {
  isOpen: PropTypes.bool,
  isEdit: PropTypes.bool,
  onCloseClick: PropTypes.func,
  editRecord: PropTypes.any
};

export default AddEditAuditorCategoryModal;
